import React from "react";
import SEO from "../components/SEO";

const About = () => {
  return (
    <>
      <SEO
        title="About"
        description="Shirts Patches"
        keywords="Shirts Patches"
      />

      <div className="min-h-screen bg-lightPastelPurple">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
          {/* Hero Section */}
          <div className="relative overflow-hidden rounded-[34px] border border-pastelPurple bg-white px-6 py-12 text-center shadow-[0_22px_60px_rgba(47,34,84,0.10)] md:px-10 md:py-16">
            <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-pastelPurple/70 blur-3xl"></div>
            <div className="absolute -right-24 -bottom-24 h-80 w-80 rounded-full bg-keyLime/25 blur-3xl"></div>

            <div className="relative mx-auto max-w-5xl">
              <span className="inline-flex rounded-full border border-pastelPurple bg-lightPastelPurple px-6 py-2 text-sm font-extrabold text-themePurple shadow-sm md:text-base">
                Premium Custom Hat Patches
              </span>

              <h1 className="mt-6 text-4xl font-extrabold leading-tight tracking-tight text-darkPurple md:text-6xl">
                About{" "}
                <span className="text-themePurple">
                  Personalised Patches
                </span>
                <br />
                <span className="text-3xl text-[#5f5870] md:text-4xl">
                  for Hats
                </span>
              </h1>

              <p className="mx-auto mt-6 max-w-3xl text-base font-medium leading-8 text-[#5f5870] md:text-xl">
                Your trusted partner for premium, durable, and beautifully crafted
                custom patches designed specifically for hats & caps.
              </p>
            </div>
          </div> 
        
          {/* Main Card */}
          <div className="mt-12 space-y-10">
            {/* Intro */}
            <section className="relative overflow-hidden rounded-[30px] border border-pastelPurple bg-white p-6 shadow-[0_18px_45px_rgba(47,34,84,0.08)] md:p-8 lg:p-10">
              <div className="absolute left-0 top-0 h-72 w-72 rounded-full bg-pastelPurple/60 blur-3xl"></div>
              <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-keyLime/20 blur-3xl"></div>

              <div className="relative mx-auto max-w-5xl">
                <h2 className="text-2xl font-extrabold leading-tight tracking-tight text-darkPurple md:text-3xl">
                  The Patch Partner Customers Trust
                </h2>

                <div className="mt-4 flex items-center gap-3">
                  <span className="h-1 w-16 rounded-full bg-keyLime"></span>
                  <span className="h-1 w-8 rounded-full bg-pastelPurple"></span>
                </div>

                <p className="mt-6 max-w-4xl text-base font-medium leading-8 text-[#5f5870] md:text-lg">
                  At <strong>Personalised Patches for Hats</strong>, we believe creating
                  custom hat patches should be effortless, fast, and consistently
                  high-quality. Whether you’re building a brand, outfitting a team,
                  or launching merchandise, we simplify the entire process — from
                  design to delivery.
                </p>
              </div>
            </section>

            {/* Services Grid */}
            <section className="relative overflow-hidden rounded-[30px] border border-pastelPurple bg-white p-6 shadow-[0_18px_45px_rgba(47,34,84,0.08)] md:p-8 lg:p-10">
              <div className="absolute left-0 top-0 h-72 w-72 rounded-full bg-pastelPurple/60 blur-3xl"></div>
              <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-keyLime/20 blur-3xl"></div>

              <div className="relative">
                <h2 className="text-2xl font-extrabold leading-tight tracking-tight text-darkPurple md:text-3xl">
                  What We Specialize In
                </h2>

                <div className="mt-4 flex items-center gap-3">
                  <span className="h-1 w-16 rounded-full bg-keyLime"></span>
                  <span className="h-1 w-8 rounded-full bg-pastelPurple"></span>
                </div>

                <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {[
                    "Embroidered, Woven, PVC, Chenille, Leather & Bullion Patches",
                    "Custom Shapes, Premium Finishes & Full-Color Designs",
                    "Patches Engineered Specifically for Hats & Caps",
                    "Easy Online Ordering in Minutes",
                    "Low Minimums & Bulk Discounts",
                    "Fast Turnaround with Rush Options",
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="rounded-2xl border border-pastelPurple bg-lightPastelPurple p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-[0_18px_40px_rgba(47,34,84,0.12)]"
                    >
                      <div className="flex items-start gap-4">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-keyLime text-sm font-extrabold text-darkPurple">
                          ✔
                        </span>
                        <p className="text-sm font-bold leading-7 text-darkPurple md:text-base">
                          {item}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Why Choose Us */}
            <section className="relative overflow-hidden rounded-[30px] border border-pastelPurple bg-darkPurple p-6 text-white shadow-[0_18px_45px_rgba(47,34,84,0.16)] md:p-8 lg:p-10">
              <div className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-pastelPurple/30 blur-3xl"></div>
              <div className="absolute -right-20 -bottom-20 h-64 w-64 rounded-full bg-keyLime/25 blur-3xl"></div>

              <div className="relative">
                <h2 className="text-2xl font-extrabold leading-tight tracking-tight text-white md:text-3xl">
                  What Sets Us Apart
                </h2>

                <div className="mt-4 h-1 w-20 rounded-full bg-keyLime"></div>

                <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <p className="rounded-2xl border border-white/10 bg-white/10 px-5 py-4 text-base font-bold leading-7 text-[#f2ecff]">
                     Instant pricing — no waiting for quotes
                  </p>
                  <p className="rounded-2xl border border-white/10 bg-white/10 px-5 py-4 text-base font-bold leading-7 text-[#f2ecff]">
                     Fast production & reliable delivery
                  </p>
                  <p className="rounded-2xl border border-white/10 bg-white/10 px-5 py-4 text-base font-bold leading-7 text-[#f2ecff]">
                     Real human support — no bots
                  </p>
                  <p className="rounded-2xl border border-white/10 bg-white/10 px-5 py-4 text-base font-bold leading-7 text-[#f2ecff]">
                     Precision craftsmanship & strict quality control
                  </p>
                </div>

                <p className="mt-8 max-w-3xl text-base font-medium leading-8 text-[#f2ecff] md:text-lg">
                  We’re more than patch manufacturers — we help brands, creators,
                  and organizations bring their identity to life with confidence.
                </p>
              </div>
            </section>

            {/* Trust & Authority */}
            <section className="relative overflow-hidden rounded-[30px] border border-pastelPurple bg-white p-6 shadow-[0_18px_45px_rgba(47,34,84,0.08)] md:p-8 lg:p-10">
              <div className="absolute left-0 top-0 h-72 w-72 rounded-full bg-pastelPurple/60 blur-3xl"></div>
              <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-keyLime/20 blur-3xl"></div>

              <div className="relative">
                <h2 className="text-2xl font-extrabold leading-tight tracking-tight text-darkPurple md:text-3xl">
                  Authority, Experience & Trust
                </h2>

                <div className="mt-4 flex items-center gap-3">
                  <span className="h-1 w-16 rounded-full bg-keyLime"></span>
                  <span className="h-1 w-8 rounded-full bg-pastelPurple"></span>
                </div>

                <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
                  {[
                    {
                      title: "Decades of Experience",
                      desc: "Serving businesses, brands & organizations across the USA.",
                    },
                    {
                      title: "Premium Quality Control",
                      desc: "Durable stitching, strong adhesion & flawless detailing.",
                    },
                    {
                      title: "Satisfaction Guaranteed",
                      desc: "Transparent communication & customer-first service.",
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="rounded-2xl border border-pastelPurple bg-lightPastelPurple p-6 text-center shadow-sm transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-[0_18px_40px_rgba(47,34,84,0.12)]"
                    >
                      <h3 className="text-lg font-extrabold text-darkPurple md:text-xl">
                        {item.title}
                      </h3>
                      <p className="mt-3 text-sm font-medium leading-7 text-[#5f5870] md:text-base">
                        {item.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Media Division */}
            <section className="relative overflow-hidden rounded-[30px] border border-pastelPurple bg-white p-6 shadow-[0_18px_45px_rgba(47,34,84,0.08)] md:p-8 lg:p-10">
              <div className="absolute left-0 top-0 h-72 w-72 rounded-full bg-pastelPurple/60 blur-3xl"></div>
              <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-keyLime/20 blur-3xl"></div>

              <div className="relative mx-auto max-w-5xl">
                <h2 className="text-2xl font-extrabold leading-tight tracking-tight text-darkPurple md:text-3xl">
                  Our Media & Publication Division
                </h2>

                <div className="mt-4 flex items-center gap-3">
                  <span className="h-1 w-16 rounded-full bg-keyLime"></span>
                  <span className="h-1 w-8 rounded-full bg-pastelPurple"></span>
                </div>

                <p className="mt-6 max-w-4xl text-base font-medium leading-8 text-[#5f5870] md:text-lg">
                  To share our expertise beyond manufacturing, we launched
                  <strong> EmbroiderCraft Studio — Powered by Personalised Patches for Hats</strong>.
                  Through in-depth tutorials, guides, and articles, we help embroidery
                  professionals, hobbyists, and businesses master the art of patch creation.
                </p>

                <p className="mt-4 text-base font-medium leading-8 text-[#5f5870] md:text-lg">
                  Our mission is simple: make professional embroidery knowledge accessible to everyone.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};
export default About;